import { Component, OnDestroy, OnInit, computed, effect, inject, signal } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { Router } from '@angular/router'
import { CamaraCapturaComponent } from '../../components/camara-captura'
import { guardarProveedor } from '../../../application/casos-de-uso'
import { SesionCapturaService } from '../../../application/sesion-captura'
import { BloqueoRecargaService } from '../../../application/bloqueo-recarga'
import { leerTextoDeImagen, extraerNombreProveedor } from '../../../infrastructure/ocr/reconocimiento-texto'
import { espacioLibreBajo } from '../../../infrastructure/util/almacenamiento'

/** Capturamos la tarjeta del proveedor y dejamos datos opcionales para completar despues. */
@Component({
  selector: 'app-nuevo-proveedor',
  standalone: true,
  imports: [FormsModule, CamaraCapturaComponent],
  templateUrl: './nuevo-proveedor.html',
})
export class NuevoProveedorComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router)
  private readonly sesion = inject(SesionCapturaService)
  private readonly bloqueoRecarga = inject(BloqueoRecargaService)

  private static readonly ID_BLOQUEO = 'nuevo-proveedor'

  readonly codigo = this.sesion.proveedorCodigo

  readonly tarjeta = signal<Blob | undefined>(undefined)
  readonly nombre = signal('')
  // Conservamos ubicacion vacia por compatibilidad con el modelo existente.
  readonly ubicacion = ''
  readonly contacto = signal('')
  readonly telefono = signal('')
  readonly guardando = signal(false)
  readonly mensajeError = signal('')
  /** Aviso proactivo — ver docs/hallazgo-almacenamiento-fotos-offline.md. */
  readonly espacioBajo = signal(false)

  // OCR opcional para sugerir el nombre sin bloquear la captura.
  readonly leyendoNombre = signal(false)
  private nombreActual = ''

  /** Borrador sin guardar — hasta el click en "Guardar" no queda nada en Dexie. */
  readonly hayTrabajoSinGuardar = computed(
    () => Boolean(this.tarjeta()) || this.nombre().trim() !== '' || this.contacto().trim() !== '' || this.telefono().trim() !== '',
  )

  constructor() {
    effect(() => { this.nombreActual = this.nombre() })
  }

  ngOnInit(): void {
    this.bloqueoRecarga.registrar(NuevoProveedorComponent.ID_BLOQUEO, this.hayTrabajoSinGuardar)
    void espacioLibreBajo().then((bajo) => this.espacioBajo.set(bajo))
  }

  ngOnDestroy(): void {
    this.bloqueoRecarga.desregistrar(NuevoProveedorComponent.ID_BLOQUEO)
  }

  async alCapturarTarjeta(blob: Blob): Promise<void> {
    this.tarjeta.set(blob)
    if (this.nombreActual.trim() !== '') return
    this.leyendoNombre.set(true)
    try {
      const texto = await leerTextoDeImagen(blob)
      const candidato = extraerNombreProveedor(texto)
      if (candidato && this.nombreActual.trim() === '') this.nombre.set(candidato)
    } catch {
      // El OCR es opcional; si falla, el usuario puede escribir el dato.
    } finally {
      this.leyendoNombre.set(false)
    }
  }

  async guardarYCapturar(): Promise<void> {
    this.guardando.set(true)
    this.mensajeError.set('')
    try {
      await guardarProveedor({
        codigo: this.codigo() ?? '',
        nombre: this.nombre(),
        ubicacion: this.ubicacion,
        contacto: this.contacto(),
        telefono: this.telefono(),
        tarjetaBlob: this.tarjeta(),
      })
      // La sesion se inicio con nombre null (nuevoProveedor() en inicio.ts) porque
      // todavia no existia — recien aca lo sabemos, para que la pantalla de
      // captura ya lo muestre sin tener que recargar nada.
      if (this.nombre().trim() !== '') this.sesion.actualizarProveedorNombre(this.nombre().trim())
      await this.router.navigateByUrl('/movil/captura')
    } catch (err) {
      // Incluye el caso de cuota llena (ver casos-de-uso.ts) — antes este
      // error quedaba sin capturar (sin try/catch), silencioso para la persona.
      this.mensajeError.set(err instanceof Error ? err.message : 'No se pudo guardar el proveedor.')
    } finally {
      this.guardando.set(false)
      void espacioLibreBajo().then((bajo) => this.espacioBajo.set(bajo))
    }
  }

  cancelar(): void {
    void this.router.navigateByUrl('/movil')
  }
}
